import QRCode from 'qrcode'

/** Renders a booking URL as a PNG data URL, sized to stay sharp when saved and re-shown. */
export async function generateQrDataUrl(url: string): Promise<string> {
  return await QRCode.toDataURL(url, {
    width: 512,
    margin: 2,
    errorCorrectionLevel: 'M',
  })
}

/** How the save ended. The UI has to say something different when it ended in `manual`. */
export type QrSaveOutcome = 'shared' | 'downloaded' | 'cancelled' | 'manual'

type ShareCapableNavigator = Navigator & {
  canShare?: (data: { files?: File[] }) => boolean
  share?: (data: { files?: File[] }) => Promise<void>
}

/**
 * Whether to reach for the share sheet before the download.
 *
 * Touch devices, because that is where a download either fails or lands somewhere the
 * customer will not find. On a desktop the download goes to a folder they know, so a
 * share sheet there would just be a surprising extra step.
 */
function prefersShareSheet(): boolean {
  return window.matchMedia?.('(pointer: coarse)')?.matches ?? false
}

/**
 * Decoded synchronously on purpose: `navigator.share` has to run inside the click that
 * triggered it, and awaiting anything first — `fetch(dataUrl)` being the tempting one —
 * spends the user gesture, after which iOS rejects the share.
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const [header, encoded] = dataUrl.split(',')
  const type = header?.match(/:(.*?);/)?.[1] || 'image/png'
  const binary = atob(encoded || '')
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }

  return new Blob([bytes], { type })
}

/**
 * Hands the QR image to the device.
 *
 * Phones get the native share sheet, where "Save Image" drops the QR into the photo
 * roll. A plain `<a download>` does not work there: iOS Safari ignores `download` on a
 * data URL and opens the image in place instead, and in-app browsers frequently block
 * downloads outright. Desktop keeps the download, and a browser with neither option
 * just opens the image so it can be saved by hand.
 */
export async function saveQrImage(dataUrl: string, filename: string): Promise<QrSaveOutcome> {
  const blob = dataUrlToBlob(dataUrl)
  const file = new File([blob], filename, { type: blob.type })
  const nav = navigator as ShareCapableNavigator

  if (prefersShareSheet() && nav.canShare?.({ files: [file] }) && nav.share) {
    try {
      await nav.share({ files: [file] })
      return 'shared'
    } catch (error) {
      // Dismissing the sheet is a decision, not a failure: do not then force a download
      // the customer just declined. Any other error falls through to the download.
      if ((error as Error)?.name === 'AbortError') return 'cancelled'
    }
  }

  // A blob URL rather than the data URL, because iOS only honours `download` on blobs.
  const objectUrl = URL.createObjectURL(blob)

  try {
    const link = document.createElement('a')

    if (!('download' in link)) {
      window.open(objectUrl, '_blank')
      return 'manual'
    }

    link.href = objectUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    return 'downloaded'
  } finally {
    // Released on a later tick so the download has read the blob first.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
  }
}
